export const ABOUT = "About Me";
export const BACKGROUND = "Background";
export const ACADEMICS = "Academics";
export const COLLAGE_RESULTS = "College Results";
export const ESSAYS = "Essays";
export const EXT_ACTIVITIES = "Extracurricular Activities";
export const HONORS_AWARDS = "Honors & Awards";
export const AP_IB_TESTS = "AP & IB Test Scores";
export const ADDITIONAL_INFO = "Additional Info Section";
export const ADVICE = "Share Advice for Applicants";
export const SCHOLARSHIP_INFO = "Scholarship Information";
export const ADMISSIONS_NOTES = "Admissions File Notes";

export const COMPLETENESS_STATUSES = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  FINISHED: "FINISHED",
};

export const getAboutMeScore = (profile) => {
  let result = 0;

  if (profile.display_name) {
    result += 1;
  }

  if (profile.sex) {
    result += 1;
  }

  if (profile.bio) {
    result += 1;
  }

  if (profile.university) {
    result += 1;
  }

  if (profile.application_year) {
    result += 1;
  }

  if (profile.academic_interests) {
    result += 1;
  }

  if (profile.approach && typeof profile.approach === "string" && profile.approach.length >= 100) {
    result += 1;
  }

  return result;
};

export const getBackgroundScore = (profile) => {
  let result = 0;

  if (profile.ethnicity && profile.ethnicity.length) {
    result += 1;
  }

  if (profile.highschool_type) {
    result += 1;
  }

  if (profile.origin_country) {
    result += 1;
  }

  // Skipping optional state (should work for the USA)
  // origin_state

  // Skipping optional checkboxes
  // is_first_gen
  // is_legacy
  // is_athlete
  // is_low_income
  // is_international

  return result;
};

export const getAcademicsScore = (profile) => {
  let result = 0;

  if (profile.gpa_uw) {
    result += 1;
  }

  if (profile.gpa_scale) {
    result += 1;
  }

  if (profile.gpa_w) {
    result += 1;
  }

  if (profile.rank_in_class) {
    result += 1;
  }

  if (profile.is_test_optional) {
    result += 1;
  } else if (profile.sat_superscore || profile.act_superscore) {
    result += 1;
  }

  return result;
};

export const getCollegeResultsScore = (profile) => {
  let result = 0;

  if (profile.colleges?.length) {
    result += 1;

    if (profile.colleges.filter((c) => !c.result).length === 0) {
      result += 1;
    }
  }

  return result;
};

export const getEssaysScore = (profile) => {
  const correctEssaysCount = (profile.essays || []).filter(
    (e) => (e?.content || "").length >= 100,
  ).length;

  return Math.min(correctEssaysCount, 2) * 2;
};

export const getActivitiesScore = (profile) => {
  const correctActivities = (profile.ecs || []).filter(
    (e) => !!e.position && !!e.organization && !!e.description,
  ).length;

  return Math.min(correctActivities, 3) * 0.5;
};

export const getHonorsScore = (profile) => {
  const correctAwardsCount = (profile.awards || []).filter((a) => a.level && a.description).length;

  return Math.min(correctAwardsCount, 1);
};

export const getAPIBTestsScore = (profile) => {
  const completeScoresCount = (profile.ap_scores || []).filter(
    (s) => !!s?.score && !!s?.name,
  ).length;

  return Math.min(completeScoresCount, 4);
};

export const getAdditionalInfoScore = (profile) => {
  let result = 0;

  if (
    profile.additional_info &&
    typeof profile.additional_info === "string" &&
    profile.additional_info.length >= 100
  ) {
    result += 1;
  }

  return result;
};

export const getAdviceScore = (profile) => {
  const correctAdviceCount = (profile.advice || []).filter(
    (a) => a.prompt && typeof a.content === "string" && a.content.length >= 100,
  ).length;

  return Math.min(correctAdviceCount, 1);
};

export const getScholarshipScore = (profile) => {
  const completeScholarshipsCount = (profile.scholarships || []).filter(
    (s) => s.name && s.hasFile,
  ).length;

  return Math.min(completeScholarshipsCount, 1);
};

export const getAdmissionsNotesScore = (profile) => {
  const completeNotes = (profile.ao_notes || []).filter(
    (n) => typeof n.overall === "string" && n.overall.length >= 100,
  ).length;

  return Math.min(completeNotes, 3);
};

export const COMPLETENESS_SECTION_MAP = {
  [ABOUT]: { id: ABOUT, handler: getAboutMeScore, maxScore: 7 },
  [BACKGROUND]: { id: BACKGROUND, handler: getBackgroundScore, maxScore: 3 },
  [ACADEMICS]: { id: ACADEMICS, handler: getAcademicsScore, maxScore: 5 },
  [COLLAGE_RESULTS]: { id: COLLAGE_RESULTS, handler: getCollegeResultsScore, maxScore: 2 },
  [ESSAYS]: { id: ESSAYS, handler: getEssaysScore, maxScore: 4 },
  [EXT_ACTIVITIES]: { id: EXT_ACTIVITIES, handler: getActivitiesScore, maxScore: 1.5 },
  [HONORS_AWARDS]: { id: HONORS_AWARDS, handler: getHonorsScore, maxScore: 1 },
  [AP_IB_TESTS]: { id: AP_IB_TESTS, handler: getAPIBTestsScore, maxScore: 4 },
  [ADDITIONAL_INFO]: { id: ADDITIONAL_INFO, handler: getAdditionalInfoScore, maxScore: 1 },
  [ADVICE]: { id: ADVICE, handler: getAdviceScore, maxScore: 1 },
  [SCHOLARSHIP_INFO]: { id: SCHOLARSHIP_INFO, handler: getScholarshipScore, maxScore: 1 },
  [ADMISSIONS_NOTES]: { id: ADMISSIONS_NOTES, handler: getAdmissionsNotesScore, maxScore: 3 },
};

export const COMPLETENESS_SECTIONS = Object.keys(COMPLETENESS_SECTION_MAP);

export const getSectionPercentage = (sectionId, profile) => {
  const { handler, maxScore } = COMPLETENESS_SECTION_MAP[sectionId];

  const score = handler(profile);

  return score / maxScore;
};

export const getSectionFlag = (sectionId, score) => {
  const { maxScore } = COMPLETENESS_SECTION_MAP[sectionId];

  if (score === 0) {
    return COMPLETENESS_STATUSES.NOT_STARTED;
  }

  if (score === maxScore) {
    return COMPLETENESS_STATUSES.FINISHED;
  }

  if (score > 0) {
    return COMPLETENESS_STATUSES.IN_PROGRESS;
  }
};

export const getSectionInfo = (sectionId) => {
  return COMPLETENESS_SECTION_MAP[sectionId];
};

export const getSectionHandler = (sectionId) => {
  return COMPLETENESS_SECTION_MAP[sectionId]?.handler;
};

export const computeCompletenessScore = (profile) => {
  if (!profile) {
    return 0;
  }

  try {
    let score = 0;

    COMPLETENESS_SECTIONS.forEach((id) => {
      const handler = getSectionHandler(id);

      try {
        const sectionScore = handler(profile);

        score += sectionScore;
      } catch (e) {
        // Figure out how to handle errors
        console.error(e);
      }
    });

    return score;
  } catch (e) {
    // Add error handling
    return 0;
  }
};

export const COMPLETENESS_TOTAL_SCORE = Object.values(COMPLETENESS_SECTION_MAP).reduce(
  (total, section) => total + section.maxScore,
  0,
);

export const getProfileCompleteness = (profileData) => {
  let score = Number(profileData.completeness_score);
  if (score && !isNaN(score)) {
    return score;
  }

  return computeCompletenessScore(profileData);
};

export const getProfileCompletenessPercent = (profileData) => {
  let score = getProfileCompleteness(profileData);
  if (typeof score !== "number") {
    score = Math.round(COMPLETENESS_TOTAL_SCORE / 2);
  }

  return score / COMPLETENESS_TOTAL_SCORE;
};

export const getProfilePay = (percent) => {
  if (percent < 0.5) {
    return 0.5;
  }

  if (percent < 0.75) {
    return 1;
  }

  return 2;
};

export const getProfileCreditsCost = (percent) => {
  if (percent < 0.5) {
    return 5;
  }

  if (percent < 0.75) {
    return 8;
  }

  return 10;
};

export const getCollegeUserPay = (profileData) => {
  const percent = getProfileCompleteness(profileData);
  return getProfilePay(percent);
};

export const getDemandUserCost = (profileData) => {
  const percent = getProfileCompleteness(profileData);
  return getProfileCreditsCost(percent);
};
